import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { useId, useState } from "react";

import ArticleContent from "@/components/marketing/article/ArticleContent";
import { FAQ_QUESTION } from "@/components/marketing/FaqRow";
import { FaqChevron } from "@/components/marketing/FaqToggleIcon";
import {
  FAQ_RAIL,
  FRAME,
  SITE_STEPS,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Faq } from "@/content/types";

/**
 * The FAQ band that closes the published post: a full-bleed grey strip whose
 * cards open one at a time. It renders outside the page container so the strip
 * can span the viewport.
 */
const HEADING = "Frequently Asked Questions";
const SUMMARY_HEIGHT = "35.19px";
/*
 * Below the site's wide step the published band tightens everything, the
 * strip's padding, the heading's leading and lead-out, the card padding and the
 * question's type, so the whole band steps down, not just its width.
 */
const CARD_TRANSITION =
  "border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)";

export default function PostFaqs({ faqs }: { faqs: Faq[] }) {
  const [open, setOpen] = useState<string | null>(null);
  // MUI reads these back onto the answer as its `id` and `aria-labelledby`.
  const bandId = useId();

  if (faqs.length === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ backgroundColor: marketingPalette.surface }}>
      <Box
        sx={(theme) => ({
          maxWidth: FRAME.header,
          mx: "auto",
          px: 3,
          // 48 below the band at the wide step and on a phone, 64 between.
          pb: "48px",
          [theme.breakpoints.down(SITE_STEPS.wide)]: { pb: 6 },
          [TINY]: { pb: "48px" },
        })}
      >
        <Stack
          sx={(theme) => ({
            maxWidth: FAQ_RAIL,
            mx: "auto",
            pt: "104px",
            [theme.breakpoints.down(SITE_STEPS.wide)]: { pt: 6 },
          })}
        >
          <Typography
            variant="h2"
            sx={(theme) => ({
              lineHeight: "48px",
              textAlign: "center",
              mb: 5,
              [theme.breakpoints.down(SITE_STEPS.wide)]: {
                lineHeight: "40px",
                mb: 3,
              },
              // `.h2_new.with-padding`, its own size on a phone rather than the
              // body headings' 26px, and led closer.
              [TINY]: { fontSize: 27, lineHeight: "35.1px" },
            })}
          >
            {HEADING}
          </Typography>

          <Stack gap={2.5}>
            {faqs.map((faq, index) => (
              <Accordion
                key={faq.question}
                disableGutters
                square
                elevation={0}
                expanded={open === faq.question}
                onChange={(_, isOpen) => setOpen(isOpen ? faq.question : null)}
                sx={(theme) => ({
                  p: 4,
                  [theme.breakpoints.down(SITE_STEPS.wide)]: { p: 3 },
                  border: 1,
                  borderColor: marketingPalette.outline,
                  borderRadius: "16px",
                  boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px 12px -5px",
                  transition: CARD_TRANSITION,
                  "&::before": { display: "none" },
                  "&:hover": { borderColor: marketingPalette.ink },
                })}
              >
                <AccordionSummary
                  id={`${bandId}-${index}-question`}
                  aria-controls={`${bandId}-${index}-answer`}
                  expandIcon={<FaqChevron />}
                  sx={{
                    p: 0,
                    gap: 3,
                    minHeight: SUMMARY_HEIGHT,
                    "&.Mui-expanded": { minHeight: SUMMARY_HEIGHT },
                  }}
                  slotProps={{
                    content: { sx: { m: 0, "&.Mui-expanded": { m: 0 } } },
                    expandIconWrapper: {
                      sx: {
                        width: SUMMARY_HEIGHT,
                        height: SUMMARY_HEIGHT,
                        alignItems: "center",
                        justifyContent: "center",
                        color: marketingPalette.ink,
                      },
                    },
                  }}
                >
                  {/* One definition, shared with the FAQ rows the landing and
                      feature pages draw: this was written twice and the two
                      copies drifted. The heading is the `h3` MUI wraps the
                      button in, as it is on those pages. */}
                  <Typography component="span" sx={FAQ_QUESTION}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pt: "13.6px" }}>
                  <Typography
                    component="div"
                    sx={{
                      textAlign: "left",
                      maxWidth: "90%",
                      // A block answer arrives as paragraphs, and their default
                      // margins would open the card past the published height.
                      "& p": { m: 0 },
                      "& p + p": { mt: 2 },
                    }}
                  >
                    <ArticleContent body={faq.answer} />
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
